import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { GoogleCallback } from "./components/GoogleCallback";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Scanner from "./pages/Scanner";
import StockDetail from "./pages/StockDetail";
import ComingSoon from "./pages/ComingSoon";
import Assistant from "./pages/Assistant";
import Watchlists from "./pages/Watchlists";
import SharedCards from "./pages/SharedCards";
import ShareCardPublic from "./pages/ShareCardPublic";
import Portfolio from "./pages/Portfolio";
import RiskTools from "./pages/RiskTools";
import Dividends from "./pages/Dividends";
import Macro from "./pages/Macro";
import Learn from "./pages/Learn";
import Lesson from "./pages/Lesson";
import TaxAssistant from "./pages/TaxAssistant";
import Simulator from "./pages/Simulator";
import Upgrade from "./pages/Upgrade";
import Methodology from "./pages/Methodology";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclaimer from "./pages/Disclaimer";

/** /share/featured (listed in sitemap.xml): resolves the current featured card and redirects. */
const FeaturedRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/share/featured")
      .then((r) => navigate(`/share/${r.data.id}`, { replace: true }))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);
  return (
    <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading example…
    </div>
  );
};

const RootGate = () => {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading terminal…
      </div>
    );
  if (user === null) return <Landing />;
  return <Navigate to="/dashboard" replace />;
};

const Protected = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading terminal…
      </div>
    );
  if (user === null) {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Layout />;
};

function AppRoutes() {
  const location = useLocation();
  // Synchronous check during render (NOT useEffect): a fresh Google session_id must be
  // processed before any route/auth logic runs.
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootGate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/methodology" element={<Methodology />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/share/featured" element={<FeaturedRedirect />} />
      <Route path="/share/:cardId" element={<ShareCardPublic />} />
      <Route element={<Protected />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/stock/:ticker" element={<StockDetail />} />
        <Route path="/assistant" element={<Assistant />} />
        <Route path="/watchlists" element={<Watchlists />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/risk" element={<RiskTools />} />
        <Route path="/dividends" element={<Dividends />} />
        <Route path="/macro" element={<Macro />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/:lessonId" element={<Lesson />} />
        <Route path="/tax" element={<TaxAssistant />} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/shared-cards" element={<SharedCards />} />
        <Route path="/module/:slug" element={<ComingSoon />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors theme="dark" />
    </AuthProvider>
  );
}

export default App;
